import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import {FilmIcon} from "../LibrettoEditor/FilmIcon";
import {CameraIcon} from "../../editor/assets/icons/CameraIcon";

// ---- shared paper shell ----
const PaperPanel: React.FC<{
    title: string;
    rightAction?: React.ReactNode;
    children: React.ReactNode;
    fill?: boolean;
}> = ({ title, rightAction, children, fill }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            background: "var(--app-bg-surface)",
            border: "0.5px solid var(--app-border-subtle)",
            borderRadius: "12px",
            px: 1,
            py: 1,
            ...(fill ? { flex: 1, minHeight: 0 } : {}),
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, py: 0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--app-text-primary)" }}>{title}</Typography>
            {rightAction}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ ...(fill ? { overflow: "auto" } : {}) }}>{children}</Box>
    </Box>
);

// ---- full-width neutral tabs ----
const TabsPills: React.FC<{
    value: "visuals" | "audio";
    onChange: (v: "visuals" | "audio") => void;
}> = ({ value, onChange }) => (
    <Box sx={{ display: "flex", gap: 0.5, p: 0.5, borderRadius: "10px", background: "var(--app-bg-subtle)", mx: 1 }}>
        <Button
            onClick={() => onChange("visuals")}
            size="small"
            disableElevation
            sx={{
                flex: 1,
                textTransform: "none",
                fontSize: 13,
                px: 1.5,
                borderRadius: "8px",
                color: "var(--app-text-primary)",
                background: value === "visuals" ? "var(--app-bg-surface)" : "transparent",
                boxShadow: value === "visuals" ? "inset 0 0 0 1px var(--app-border-subtle)" : "none",
            }}
        >
            Visuals
        </Button>
        <Button
            onClick={() => onChange("audio")}
            size="small"
            disableElevation
            sx={{
                flex: 1,
                textTransform: "none",
                fontSize: 13,
                px: 1.5,
                borderRadius: "8px",
                color: "var(--app-text-primary)",
                background: value === "audio" ? "var(--app-bg-surface)" : "transparent",
                boxShadow: value === "audio" ? "inset 0 0 0 1px var(--app-border-subtle)" : "none",
            }}
        >
            Audio
        </Button>
    </Box>
);

// ---- visual thumb ----
const VisualThumb: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            height: 92,
            borderRadius: "10px",
            background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.22) 100%)",
            cursor: "pointer",
            "&:hover": { opacity: 0.9 },
        }}
    />
);

// ---- audio row ----
const AudioRow: React.FC<{
    title: string;
    duration: string;
    onPlay?: () => void;
    onAdd?: () => void;
}> = ({ title, duration, onPlay, onAdd }) => (
    <ListItem disablePadding sx={{ "&:hover": { background: "var(--app-bg-subtle)", borderRadius: "8px" } }}>
        <ListItemButton sx={{ flexDirection: "column", alignItems: "stretch", py: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pb: 0.5 }}>
                <ListItemText primary={title} primaryTypographyProps={{ fontSize: 14, noWrap: true, color: "var(--app-text-primary)" }} />
                <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", ml: 1 }}>{duration}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                <IconButton onClick={onPlay} size="small">
                    <PlayArrowRoundedIcon fontSize="small" htmlColor="var(--app-text-primary)" />
                </IconButton>
                <Box sx={{ flex: 1, height: 6, borderRadius: 999, background: "var(--app-border-subtle)" }}>
                    <Box sx={{ width: "35%", height: "100%", borderRadius: 999, background: "var(--app-text-subtle)" }} />
                </Box>
                <IconButton onClick={onAdd} size="small">
                    <AddRoundedIcon fontSize="small" htmlColor="var(--app-text-primary)" />
                </IconButton>
            </Box>
        </ListItemButton>
    </ListItem>
);

// ---- expanded state id ----
type ExpandedId =
    | null
    | "visuals:Video"
    | "visuals:Images"
    | "audio:Music"
    | "audio:Sound effects";

export const V2StockSidebar: React.FC = () => {
    const [tab, setTab] = useState<"visuals" | "audio">("visuals");
    const [expanded, setExpanded] = useState<ExpandedId>(null);

    // mock data
    const videoItems = useMemo(() => Array.from({ length: 12 }, (_, i) => ({ id: `v${i}` })), []);
    const imageItems = useMemo(() => Array.from({ length: 12 }, (_, i) => ({ id: `i${i}` })), []);
    const musicItems = useMemo(
        () => [
            { id: "m1", title: "Reggae Is The Answer (Instrumental)", duration: "02:26" },
            { id: "m2", title: "Never Be the Same Again (Instrumental)", duration: "02:53" },
            { id: "m3", title: "Driving My Car (no vocal)", duration: "02:32" },
            { id: "m4", title: "In Acoustic Clouds", duration: "01:03" },
            { id: "m5", title: "Deep Focus Loops", duration: "01:42" },
        ],
        []
    );
    const sfxItems = useMemo(
        () => [
            { id: "s1", title: "Cinematic Whoosh Impact Reveal 4", duration: "00:05" },
            { id: "s2", title: "Evil laugh 7", duration: "00:03" },
            { id: "s3", title: "Magical Fireball Whoosh", duration: "00:02" },
            { id: "s4", title: "Cinematic Whoosh To Sub Drop 6", duration: "00:03" },
            { id: "s5", title: "Glass Ping", duration: "00:01" },
        ],
        []
    );

    // keep your shared title style
    const titleTextStyle: React.CSSProperties = {
        color: "var(--app-text-primary)",
        paddingLeft: "8px",
        paddingRight: "8px",
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "20px",
        fontFamily: "Inter, sans-serif",
        userSelect: "none",
    };

    const handleTabChange = (next: "visuals" | "audio") => {
        setTab(next);
        setExpanded(null); // leaving expanded view when switching tabs
    };

    const isExpanded = (id: ExpandedId) => expanded === id;

    return (
        <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "8px", p: "3px" }}>
            {/* Title row stays the same always */}
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", marginLeft: "8px" }}>
                <Box sx={{width: '24px', height: '24px', color: 'var(--app-icon-primary)'}}>
                    <CameraIcon/>
                </Box>
                <span style={titleTextStyle}>Stock</span>
            </Box>

            {/* Tabs always visible */}
            <TabsPills value={tab} onChange={handleTabChange} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minHeight: 0 }}>
                {/* VISUALS */}
                {tab === "visuals" && (
                    <>
                        {/* When expanded, render only that section with fill; otherwise render both */}
                        {!isExpanded("visuals:Video") && !isExpanded("visuals:Images") && (
                            <>
                                <PaperPanel
                                    title="Video"
                                    rightAction={
                                        <Button
                                            size="small"
                                            onClick={() => setExpanded("visuals:Video")}
                                            sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                        >
                                            Show all
                                        </Button>
                                    }
                                >
                                    <Grid container spacing={1.25}>
                                        {videoItems.slice(0, 4).map((v) => (
                                            <Grid item xs={6} key={v.id}>
                                                <VisualThumb onClick={() => { /* TODO add video */ }} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </PaperPanel>

                                <PaperPanel
                                    title="Images"
                                    rightAction={
                                        <Button
                                            size="small"
                                            onClick={() => setExpanded("visuals:Images")}
                                            sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                        >
                                            Show all
                                        </Button>
                                    }
                                    fill
                                >
                                    <Grid container spacing={1.25}>
                                        {imageItems.slice(0, 4).map((i) => (
                                            <Grid item xs={6} key={i.id}>
                                                <VisualThumb onClick={() => { /* TODO add image */ }} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </PaperPanel>
                            </>
                        )}

                        {isExpanded("visuals:Video") && (
                            <PaperPanel
                                title="Video"
                                rightAction={
                                    <Button
                                        size="small"
                                        onClick={() => setExpanded(null)}
                                        sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                    >
                                        Show less
                                    </Button>
                                }
                                fill
                            >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pr: 0.5 }}>
                                    {videoItems.map((it) => (
                                        <Box key={it.id}>
                                            <VisualThumb onClick={() => { /* TODO add visual */ }} />
                                        </Box>
                                    ))}
                                </Box>
                            </PaperPanel>
                        )}

                        {isExpanded("visuals:Images") && (
                            <PaperPanel
                                title="Images"
                                rightAction={
                                    <Button
                                        size="small"
                                        onClick={() => setExpanded(null)}
                                        sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                    >
                                        Show less
                                    </Button>
                                }
                                fill
                            >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pr: 0.5 }}>
                                    {imageItems.map((it) => (
                                        <Box key={it.id}>
                                            <VisualThumb onClick={() => { /* TODO add visual */ }} />
                                        </Box>
                                    ))}
                                </Box>
                            </PaperPanel>
                        )}
                    </>
                )}

                {/* AUDIO */}
                {tab === "audio" && (
                    <>
                        {!isExpanded("audio:Music") && !isExpanded("audio:Sound effects") && (
                            <>
                                <PaperPanel
                                    title="Music"
                                    rightAction={
                                        <Button
                                            size="small"
                                            onClick={() => setExpanded("audio:Music")}
                                            sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                        >
                                            Show all
                                        </Button>
                                    }
                                >
                                    <List dense sx={{ py: 0 }}>
                                        {musicItems.slice(0, 4).map((m) => (
                                            <AudioRow key={m.id} title={m.title} duration={m.duration} onPlay={() => {}} onAdd={() => {}} />
                                        ))}
                                    </List>
                                </PaperPanel>

                                <PaperPanel
                                    title="Sound effects"
                                    rightAction={
                                        <Button
                                            size="small"
                                            onClick={() => setExpanded("audio:Sound effects")}
                                            sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                        >
                                            Show all
                                        </Button>
                                    }
                                    fill
                                >
                                    <List dense sx={{ py: 0 }}>
                                        {sfxItems.slice(0, 4).map((s) => (
                                            <AudioRow key={s.id} title={s.title} duration={s.duration} onPlay={() => {}} onAdd={() => {}} />
                                        ))}
                                    </List>
                                </PaperPanel>
                            </>
                        )}

                        {isExpanded("audio:Music") && (
                            <PaperPanel
                                title="Music"
                                rightAction={
                                    <Button
                                        size="small"
                                        onClick={() => setExpanded(null)}
                                        sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                    >
                                        Show less
                                    </Button>
                                }
                                fill
                            >
                                <List dense sx={{ py: 0 }}>
                                    {musicItems.map((a) => (
                                        <AudioRow key={a.id} title={a.title} duration={a.duration} onPlay={() => {}} onAdd={() => {}} />
                                    ))}
                                </List>
                            </PaperPanel>
                        )}

                        {isExpanded("audio:Sound effects") && (
                            <PaperPanel
                                title="Sound effects"
                                rightAction={
                                    <Button
                                        size="small"
                                        onClick={() => setExpanded(null)}
                                        sx={{ textTransform: "none", fontSize: 12, color: "var(--app-text-muted)" }}
                                    >
                                        Show less
                                    </Button>
                                }
                                fill
                            >
                                <List dense sx={{ py: 0 }}>
                                    {sfxItems.map((a) => (
                                        <AudioRow key={a.id} title={a.title} duration={a.duration} onPlay={() => {}} onAdd={() => {}} />
                                    ))}
                                </List>
                            </PaperPanel>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default V2StockSidebar;
